import { useState } from "react";

import { ProvenanceActComposer } from "./ProvenanceActComposer.tsx";
import {
  DOCUMENT_REJECTION_COMPOSER,
  DOCUMENT_VERDICT_LABELS,
} from "./provenance.constants.ts";
import type { DocumentVerdictModel } from "./provenance.model.ts";

/**
 * Accept or reject ONE paper — OND-62.
 *
 * The estate works through a submitted dossier and gives each document a
 * verdict. It used to have two buttons for four papers, one level up on the
 * whole entry, so disputing marked the genuine invoice disputed alongside the
 * forgery and confirming put the studio's name behind the forged certificate.
 *
 * WHY REJECT OPENS A FIELD AND ACCEPT DOES NOT. A rejection must say why — the
 * database refuses one with no reason — and an accusation typed into a
 * `window.confirm` is not a reason. `ProvenanceActComposer` is the field the
 * entry's own dispute already uses, reused rather than copied, so the refusal
 * lands beside the text that caused it and the words survive a refused attempt.
 *
 * THE ONLY STATE HERE IS WHETHER THE FIELD IS OPEN. Whether the controls appear
 * at all, which one reads as held, and what the counts say are all the model's —
 * `documentVerdictFor` decided them, and there is no business condition in this
 * file.
 */
export function DocumentVerdict({
  verdict,
  name,
  onAccept,
  onReject,
  onUndo,
}: {
  verdict: DocumentVerdictModel;
  /** The paper's own name, so each control is distinguishable to a screen reader. */
  name: string;
  onAccept: () => void;
  onReject: (reason: string) => string | null;
  onUndo: () => void;
}) {
  const [rejecting, setRejecting] = useState(false);

  return (
    <div className="provenance__verdict">
      {/* Empty when nobody has judged it, which is nothing said rather than a
          score of nil-nil — so there is no element to read as one. */}
      {verdict.summary !== "" && (
        <span className="provenance__verdict-count">{verdict.summary}</span>
      )}

      {verdict.canJudge && (
        <div className="provenance__verdict-acts">
          <button
            type="button"
            className="provenance__verdict-act"
            aria-pressed={verdict.mine === "verify"}
            aria-label={`${DOCUMENT_VERDICT_LABELS.verify}: ${name}`}
            onClick={onAccept}
          >
            {DOCUMENT_VERDICT_LABELS.verify}
          </button>
          <button
            type="button"
            className="provenance__verdict-act"
            aria-pressed={verdict.mine === "dispute"}
            aria-label={`${DOCUMENT_VERDICT_LABELS.dispute}: ${name}`}
            onClick={() => setRejecting(true)}
          >
            {DOCUMENT_VERDICT_LABELS.dispute}
          </button>
          {/* Only the reader's own live verdict can be ended, and `mineId` is
              non-null exactly when `mine` is — the server sets both off one row. */}
          {verdict.mineId !== null && (
            <button
              type="button"
              className="provenance__link"
              aria-label={`${DOCUMENT_VERDICT_LABELS.withdraw}: ${name}`}
              onClick={onUndo}
            >
              {DOCUMENT_VERDICT_LABELS.withdraw}
            </button>
          )}
        </div>
      )}

      {rejecting && (
        <ProvenanceActComposer
          composer={DOCUMENT_REJECTION_COMPOSER}
          onRecord={onReject}
          onCancel={() => setRejecting(false)}
        />
      )}
    </div>
  );
}
